// FilterMobileView.js
import React, { useState, useCallback, useEffect } from 'react';
import {
    Accordion,
    Form,
    Button,
    Modal,
    FormControl
} from 'react-bootstrap';
import InstagramSvg from '../assets/icons/Instagram_24px.svg';
import TikTokSvg from '../assets/icons/Tiktok_24px.svg';
import redHeart from '../assets/icons/red-heart-icon.svg';
import greyHeart from '../assets/icons/heart-thin-icon.svg';
import searchIcon from '../assets/icons/Search_24px.png';
import '../styles/FilterMobileView.css';
import { nicheCategories } from '../utils/nicheCategory';
import RemoveFilter from '../assets/icons/Close_24px.svg';

export default function FilterMobileView({ influencers, onFilter }) {
    const [showFilter, setShowFilter] = useState(false);

    // ─── all filter states ─────────────────────────────────────────────
    const [minFollowers, setMinFollowers] = useState('');
    const [maxFollowers, setMaxFollowers] = useState('');
    const [gender, setGender] = useState('');
    const [nicheSearch, setNicheSearch] = useState('');
    const [nicheSuggestions, setNicheSuggestions] = useState([]);
    const [showNicheSuggestions, setShowNicheSuggestions] = useState(false);
    const [creatorSearch, setCreatorSearch] = useState('');

    const [countrySearch, setCountrySearch] = useState('');
    const [countrySuggestions, setCountrySuggestions] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);

    const [languageSearch, setLanguageSearch] = useState('');
    const [languageSuggestions, setLanguageSuggestions] = useState([]);
    const [selectedLanguages, setSelectedLanguages] = useState([]);

    const [platforms, setPlatforms] = useState({ Instagram: false, Tiktok: false });
    const [minRate, setMinRate] = useState('');
    const [maxRate, setMaxRate] = useState('');
    const [minAge, setMinAge] = useState('');
    const [maxAge, setMaxAge] = useState('');
    const [hasPhone, setHasPhone] = useState('');
    const [favoriteFilter, setFavoriteFilter] = useState(null);

    const [activeKey, setActiveKey] = useState(null);

    // derive full lists
    const allCountries = [...new Set(influencers.map(i => i.currentlyLiving).filter(Boolean))];
    const allLanguages = [...new Set(
        influencers.flatMap(i => (i.languages || '').split(',')).filter(Boolean)
    )];

    // ─── close accordion on scroll ─────────────────────────────────────
    useEffect(() => {
        const c = () => setActiveKey(null);
        window.addEventListener('scroll', c);
        return () => window.removeEventListener('scroll', c);
    }, []);

    // ─── filter logic ─────────────────────────────────────────────────
    const applyFilter = useCallback(() => {
        const filtered = influencers.filter(inf => {
            const f = +inf.followers || 0;
            const r = parseFloat((inf.rate || '').replace(/[^0-9.]/g, '')) || 0;
            const a = +inf.age || 0;
            if (minFollowers && f < +minFollowers) return false;
            if (maxFollowers && f > +maxFollowers) return false;
            if (platforms.Instagram && !inf.instagramUsername) return false;
            if (platforms.Tiktok && !inf.tikTokUsername) return false;
            if (favoriteFilter !== null && inf.favorite !== favoriteFilter) return false;
            if (minRate && r < +minRate) return false;
            if (maxRate && r > +maxRate) return false;
            if (gender && inf.gender?.toLowerCase() !== gender.toLowerCase()) return false;
            if (minAge && a < +minAge) return false;
            if (maxAge && a > +maxAge) return false;
            if (hasPhone === 'yes' && !inf.phone) return false;
            if (hasPhone === 'no' && inf.phone) return false;
            // country multi
            if (selectedCountries.length > 0 && !selectedCountries.includes(inf.currentlyLiving)) return false;
            // language multi
            if (selectedLanguages.length > 0) {
                const langs = (inf.languages || '').split(',').map(l => l.trim());
                if (!selectedLanguages.some(sl => langs.includes(sl))) return false;
            }
            if (creatorSearch &&
                ![inf.creatorName, inf.instagramUsername, inf.tikTokUsername]
                    .some(s => s?.toLowerCase().includes(creatorSearch.toLowerCase()))) return false;
            if (nicheSearch) {
                const terms = nicheSearch.split(',').map(t => t.trim().toLowerCase());
                if (!terms.every(t => inf.niches?.some(n => n.toLowerCase().includes(t)))) return false;
            }
            return true;
        });
        onFilter(filtered);
        setShowFilter(false);
    }, [
        influencers,
        minFollowers, maxFollowers,
        platforms, favoriteFilter,
        minRate, maxRate,
        gender,
        minAge, maxAge,
        hasPhone,
        selectedCountries, selectedLanguages,
        creatorSearch, nicheSearch,
        onFilter
    ]);

    const resetFilter = () => {
        setMinFollowers(''); setMaxFollowers('');
        setPlatforms({ Instagram: false, Tiktok: false });
        setFavoriteFilter(null);
        setMinRate(''); setMaxRate('');
        setGender('');
        setMinAge(''); setMaxAge('');
        setHasPhone('');
        setCountrySearch(''); setSelectedCountries([]);
        setLanguageSearch(''); setSelectedLanguages([]);
        setCreatorSearch(''); setNicheSearch('');
        onFilter(influencers);
        setShowFilter(false);
    };

    // ─── suggestions ───────────────────────────────────────────────────
    const handleNicheSearch = val => {
        setNicheSearch(val);
        if (!val) return setShowNicheSuggestions(false);
        const m = nicheCategories.filter(cat =>
            cat.category.toLowerCase().includes(val.toLowerCase()) ||
            cat.subcategories.some(s => s.toLowerCase().includes(val.toLowerCase()))
        );
        setNicheSuggestions(m);
        setShowNicheSuggestions(true);
    };

    const handleCountrySearch = val => {
        setCountrySearch(val);
        if (!val && activeKey === 'country') {
            setCountrySuggestions(allCountries);
            return;
        }
        if (val.length >= 1) {
            setCountrySuggestions(allCountries.filter(c => c.toLowerCase().includes(val.toLowerCase())));
        } else {
            setCountrySuggestions([]);
        }
    };

    const handleLanguageSearch = val => {
        setLanguageSearch(val);
        console.log(val)
        if (!val && activeKey === 'language') {
            setLanguageSuggestions(allLanguages);
            console.log(val)
            return;
        }
        if (val.length >= 1) {
            setLanguageSuggestions(allLanguages.filter(l => l.toLowerCase().includes(val.toLowerCase())));
        } else {
            setLanguageSuggestions([]);
        }
    };

    const pickCountry = c => {
        setSelectedCountries(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
    };
    const pickLanguage = l => {
        setSelectedLanguages(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);
    };
    const togglePlatform = p =>
        setPlatforms(prev => ({ ...prev, [p]: !prev[p] }));
    // ─ Pills builder ────────────────────────────────────────────────────
    const pills = [];

    if (minFollowers || maxFollowers) {
        pills.push({ key: 'followers', label: `${minFollowers || 0}-${maxFollowers || '∞'}`, clear: () => { setMinFollowers(''); setMaxFollowers(''); applyFilter(); } });
    }
    if (platforms.Instagram) pills.push({ key: 'insta', label: 'Instagram', clear: () => { togglePlatform('Instagram'); applyFilter() } });
    if (platforms.Tiktok) pills.push({ key: 'tt', label: 'TikTok', clear: () => { togglePlatform('Tiktok'); applyFilter() } });
    if (favoriteFilter !== null) pills.push({ key: 'fav', label: favoriteFilter ? 'Favorite' : 'Not Favorite', clear: () => { setFavoriteFilter(null); applyFilter(); } });
    if (minRate || maxRate) pills.push({ key: 'rate', label: `${minRate || 0}-${maxRate || '∞'}`, clear: () => { setMinRate(''); setMaxRate(''); applyFilter(); } });
    if (gender) pills.push({ key: 'gender', label: gender, clear: () => { setGender(''); applyFilter(); } });
    if (minAge || maxAge) pills.push({ key: 'age', label: `${minAge || 0}-${maxAge || '∞'}`, clear: () => { setMinAge(''); setMaxAge(''); applyFilter(); } });
    if (hasPhone) pills.push({ key: 'phone', label: hasPhone === 'yes' ? 'Has Phone' : 'No Phone', clear: () => { setHasPhone(''); applyFilter(); } });
    // multi-country pills
    selectedCountries.forEach((c, i) =>
        pills.push({ key: `country-${i}`, label: c, clear: () => { setSelectedCountries(prev => prev.filter(x => x !== c)); applyFilter(); } })
    );
    // multi-language pills
    selectedLanguages.forEach((l, i) =>
        pills.push({ key: `lang-${i}`, label: l, clear: () => { setSelectedLanguages(prev => prev.filter(x => x !== l)); applyFilter(); } })
    );
    if (creatorSearch) pills.push({ key: 'creator', label: creatorSearch, clear: () => { setCreatorSearch(''); applyFilter(); } });
    nicheSearch.split(',').map(s => s.trim()).filter(Boolean).forEach((n, i) => pills.push({
        key: `niche-${i}`, label: n, clear: () => { setNicheSearch(ns => ns.split(',').map(t => t.trim()).filter(t => t !== n).join(',')); applyFilter(); }
    }));
    // ─── render ─────────────────────────────────────────────────────────
    return (
        <div className='mobile-filter'>
            <div className="position-relative" style={{ width: '100%', marginBottom: '10px' }}>
                <button
                    onClick={() => setShowFilter(true)}
                    className="filter-button flex items-center justify-between w-100"
                >
                    <span className="filter-text">Filter</span>
                    <img src="/Filter.png" alt="Filter Icon" className="w-5 h-5 mr-2" />

                </button>
            </div>

            <Modal
                show={showFilter}
                onHide={() => setShowFilter(false)}
                dialogClassName="bottom-modal"
                backdrop="static"
                className='filter-modal'
            >
                <Modal.Header closeButton>
                    <Modal.Title>Filter</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Accordion
                        activeKey={activeKey}
                        onSelect={k => setActiveKey(k)}
                    >
                        {/* ─ Pills ─ */}
                        <div>
                            {pills.length > 0 && (
                                <div className="applied-pills">
                                    {pills.map(p => (
                                        <div className="pill-content" key={p.key}>
                                            <span className="pill">{p.label}</span>
                                            <button className="pill-close" onClick={p.clear}>
                                                <img src={RemoveFilter} alt="remove" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Followers */}
                        <Accordion.Item eventKey="followers">
                            <Accordion.Header>
                                Followers {minFollowers && maxFollowers ? `${minFollowers}–${maxFollowers}` : ''}
                            </Accordion.Header>
                            <Accordion.Body className='parallel-input'>
                                <div className="accord-input">
                                    <Form.Label className='accord-label'>From</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={minFollowers}
                                        onChange={e => setMinFollowers(e.target.value)}
                                        className=""
                                    /></div><div className="accord-input">
                                    <Form.Label className='accord-label'>To</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={maxFollowers}
                                        onChange={e => setMaxFollowers(e.target.value)}
                                    /></div>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Platform */}
                        <Accordion.Item eventKey="platform">
                            <Accordion.Header>
                                Platform
                            </Accordion.Header>
                            <Accordion.Body>
                                <label
                                    className="d-flex align-items-center gap-2 my-2 accord-form-input-label"
                                    onClick={e => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        className="custom-checkbox"
                                        checked={platforms.Instagram}
                                        onChange={() => togglePlatform('Instagram')}
                                    />
                                    <img src={InstagramSvg} alt="Instagram" width={20} height={20} />
                                    Instagram
                                </label>
                                <label
                                    className="d-flex align-items-center gap-2 my-2 accord-form-input-label"
                                    onClick={e => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        className="custom-checkbox"
                                        checked={platforms.Tiktok}
                                        onChange={() => togglePlatform('Tiktok')}
                                    />
                                    <img src={TikTokSvg} alt="TikTok" width={20} height={20} />
                                    TikTok
                                </label>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Favorite */}
                        <Accordion.Item eventKey="favorite">
                            <Accordion.Header>
                                Favorite
                            </Accordion.Header>
                            <Accordion.Body>
                <div onClick={()=>setFavoriteFilter(true)} className='check-label accord-form-input-label my-2'>
                  <img src={redHeart} width={16} alt="Fav" className='me-2'/> Favorite
                </div>
                <div onClick={()=>setFavoriteFilter(false)} className='check-label accord-form-input-label my-2'>
                  <img src={greyHeart} width={17} alt="NotFav" className='me-2'/> Not Favorite
                </div>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Rate */}
                        <Accordion.Item eventKey="rate">
                            <Accordion.Header>
                                Rate
                            </Accordion.Header>
                            <Accordion.Body className='parallel-input'>
                                <div className="accord-input">
                                    <Form.Label className='accord-label'>Min</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={minRate}
                                        onChange={e => setMinRate(e.target.value)}
                                        className=""
                                    /></div><div className="accord-input">
                                    <Form.Label className='accord-label'>Max</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={maxRate}
                                        onChange={e => setMaxRate(e.target.value)}
                                    /></div>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Gender */}
                        <Accordion.Item eventKey="gender">
                            <Accordion.Header>
                                Gender {gender}
                            </Accordion.Header>
                            <Accordion.Body>
                            <div onClick={() => setGender('All')} className='check-label accord-form-input-label my-2'>
                  All
                </div>
                            <div onClick={() => setGender('Male')} className='check-label accord-form-input-label my-2'>
                  Male
                </div>
                <div onClick={() => setGender('Female')} className='check-label accord-form-input-label my-2'>
                   Female
                </div>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Age */}
                        <Accordion.Item eventKey="age">
                            <Accordion.Header>
                                Age
                            </Accordion.Header>
                            <Accordion.Body className='parallel-input'>
                                <div className="accord-input">
                                    <Form.Label className='accord-label'>Min</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={minAge}
                                        onChange={e => setMinAge(e.target.value)}
                                        className=""
                                    /></div><div className="accord-input">
                                    <Form.Label className='accord-label'>Max</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={maxAge}
                                        onChange={e => setMaxAge(e.target.value)}
                                    /></div>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Phone */}
                        <Accordion.Item eventKey="phone">
                            <Accordion.Header>
                                Phone
                            </Accordion.Header>
                            <Accordion.Body>
                                {['All', 'Has Phone', 'No Phone'].map(opt => (
                                    <div
                                        key={opt}
                                        name="phone-group"
                                
                                        onClick={() => setHasPhone(
                                            opt === 'All' ? ''
                                                : opt === 'Has Phone' ? 'yes'
                                                    : 'no'
                                        )}
                                        className="check-label accord-form-input-label my-2"
                                    >{opt}</div>
                                ))}
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Country */}
                        <Accordion.Item eventKey="country" onClick={()=>setCountrySuggestions(allCountries)}>
                            <Accordion.Header>
                                Country
                            </Accordion.Header>
                            <Accordion.Body className="country-search-container border rounded-5 mt-3">
                                <Form.Label className='accord-label' >Search</Form.Label>
                                <FormControl
                                    value={countrySearch}
                                    onChange={e => handleCountrySearch(e.target.value)}
                                    placeholder="Type to filter…"
                                    className="mb-2"
                                />
                                <div className="suggestions-dropdown-items">
                                    {countrySuggestions.map((c, i) => (
                                        <label
                                            key={i}
                                            className="d-flex align-items-center gap-2 suggestions-dropdown-item"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedCountries.includes(c)}
                                                onChange={() => pickCountry(c)}
                                                className='custom-checkbox w-auto d-none'
                                            />
                                            {c}
                                        </label>
                                    ))}
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Languages */}
                        <Accordion.Item eventKey="language">
                            <Accordion.Header onClick={()=>setLanguageSuggestions(allLanguages)}>
                                Languages
                            </Accordion.Header>
                            <Accordion.Body className="language-search-container border rounded-5 mt-3">
                                <Form.Label className='accord-label'>Search</Form.Label>
                                <FormControl
                                    value={languageSearch}
                                    onChange={e => handleLanguageSearch(e.target.value)}
                                    placeholder="Type to filter…"
                                    className="mb-2"
                                />
                                <div className="suggestions-dropdown-items">
                                    {languageSuggestions.map((l, i) => (
                                        <label
                                            key={i}
                                            className="d-flex align-items-center gap-2 suggestions-dropdown-item"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedLanguages.includes(l)}
                                                onChange={() => pickLanguage(l)}
                                                className='custom-checkbox w-auto'
                                            />
                                            {l}
                                        </label>
                                    ))}
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Modal.Body>

                <Modal.Footer className="d-flex align-items-center justify-content-between px-3 save-cancel-btn-div">
                    <button className="cancel-btn w-auto" onClick={() => resetFilter()}>Reset Filter</button>
                    <button className="save-btn" onClick={applyFilter}>Apply</button>
                </Modal.Footer>
            </Modal>

            {/* ─ bottom niche + creator search + Get Results ───────────── */}
            <div className="filter-container">
                <div className="filter-section-mobile d-flex flex-wrap align-items-center" style={{ width: '100%' }}>
                    {/* Niche Search */}
                    <div className="position-relative w-100 mb-2">
                        <img src={searchIcon} alt="Search" className="custom-search-icon" />
                        <input
                            type="text"
                            placeholder="Search Niches (Separate by comma)"
                            value={nicheSearch}
                            onChange={e => handleNicheSearch(e.target.value)}
                            className="custom-search-input-mobile ps-5"
                        />
                        {showNicheSuggestions && nicheSuggestions.length > 0 && (
                            <div className="niche-suggestions">
                                {nicheSuggestions.map((cat, idx) => (
                                    <div key={idx}>
                                        <div className="suggestion-header" onClick={() => {/*...*/ }}>
                                            {cat.category}
                                        </div>
                                        {cat.subcategories.map((sub, j) => (
                                            <div key={j} className="suggestion-item" onClick={() => {/*...*/ }}>
                                                {sub}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Creator Search */}
                    <div className="position-relative w-100 mb-2">
                        <img src={searchIcon} alt="Search" className="custom-search-icon" />
                        <input
                            type="text"
                            placeholder="Search Creator (Separate by comma)"
                            value={creatorSearch}
                            onChange={e => setCreatorSearch(e.target.value)}
                            className="custom-search-input-mobile ps-5"
                        />
                    </div>

                    {/* Get Results */}
                    <Button
                        onClick={applyFilter}
                        className="w-100"
                        style={{
                            backgroundColor: '#875FFE',
                            borderRadius: '20px',
                            fontWeight: '600',
                            padding: '10px 24px',
                            fontSize: '14px',
                            boxShadow: '0px 2px 0px #6f4ad4',
                            height: '40px'
                        }}
                    >
                        Get Results
                    </Button>
                </div>
            </div>
        </div>
    );
}
